import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LexiconModule } from '@galaxy/lexicon';
import { Observable } from 'rxjs';
import { AccountGroupService } from './account-group';
import { PartnerService } from './core';
import { SalesContact, SalesContactService } from './sales-contact';

@Component({
  selector: 'bc-request-assistance-dialog',
  template: `
    <h2 mat-dialog-title>
      {{ 'REQUEST_ASSISTANCE_MODAL.HEADER' | translate }}
    </h2>
    <mat-dialog-content>
      <mat-card appearance="outlined" style="margin: -16px -23px 0px -23px">
        @if (salesContact$ | async; as salesContact) {
          <mat-card-header>
            @if (salesContact?.photoUrlSecure) {
              <div
                mat-card-avatar
                class="sales-contact-icon"
                [ngStyle]="{
                  'background-image': 'url(' + salesContact?.photoUrlSecure + '?s=120)',
                  height: '70px',
                  width: '70px',
                }"
              ></div>
            }
            @if (!salesContact?.photoUrlSecure) {
              <div
                mat-card-avatar
                class="sales-contact-icon"
                style="background-image: url('/static/images/marketing/default_sales_person.jpg')"
              ></div>
            }
            <mat-card-title style="margin-bottom:20px">
              {{ salesContact?.brandName || 'Contact Your Representative' }}
            </mat-card-title>
            <mat-card-subtitle style="margin-bottom: 4px;">
              {{ salesContact?.formattedName }}
            </mat-card-subtitle>
            <mat-card-subtitle style="margin-bottom: 4px;">
              {{ salesContact?.jobTitle }}
            </mat-card-subtitle>
            <mat-card-subtitle style="margin-bottom: 4px;">
              <a [href]="'tel:' + salesContact?.rawPhoneNumber" tabIndex="-1">
                {{ salesContact?.mainPhoneNumber }}
              </a>
            </mat-card-subtitle>
            <mat-card-subtitle style="margin-bottom: 8px;">
              <a [href]="'mailto:' + salesContact?.email" tabIndex="-1">
                {{ salesContact?.email }}
              </a>
            </mat-card-subtitle>
          </mat-card-header>
        } @else {
          <mat-card-header>
            <div
              mat-card-avatar
              class="sales-contact-icon"
              style="background-image: url('/static/images/marketing/default_sales_person.jpg')"
            ></div>
            <mat-card-subtitle>
              {{ partnerName$ | async }}
            </mat-card-subtitle>
          </mat-card-header>
        }

        <mat-card-content>
          <p style="margin-top: 10px;">
            {{ 'REQUEST_ASSISTANCE_MODAL.CONTENT' | translate }}
          </p>
          <mat-form-field style="width: 100%;">
            <textarea
              matInput
              #message
              value=""
              (blur)="activationMessage = message.value"
              placeholder="{{ 'REQUEST_ASSISTANCE_MODAL.COMPOSE_MESSAGE' | translate }}"
              matTextareaAutosize="true"
              matAutosizeMinRows="1"
              matAutosizeMaxRows="5"
              [maxLength]="500"
            ></textarea>
            <mat-hint align="end">{{ message.value.length }} / 500</mat-hint>
          </mat-form-field>
        </mat-card-content>
      </mat-card>
    </mat-dialog-content>
    <mat-dialog-actions>
      <button mat-button color="primary" (click)="close()">
        {{ 'COMMON.ACTION_LABELS.CANCEL' | translate }}
      </button>
      <button mat-raised-button color="primary" (click)="submit()">
        {{ 'COMMON.ACTION_LABELS.SEND' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .sales-contact-icon {
        background-size: cover;
        margin-left: 8px;
        margin-right: 16px;
        width: 70px;
        height: 70px;
        border-radius: 50%;
        flex-shrink: 0;
      }
    `,
  ],
  imports: [
    CommonModule,
    LexiconModule,
    MatDialogModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
})
export class RequestAssistanceComponent {
  activationMessage = '';

  salesContact$: Observable<SalesContact>;
  partnerName$: Observable<string>;
  accountGroupId: string;

  constructor(
    public dialogRef: MatDialogRef<RequestAssistanceComponent>,
    private salesContactService: SalesContactService,
    private http: HttpClient,
    private accountGroupService: AccountGroupService,
    private partnerService: PartnerService,
  ) {
    this.salesContact$ = this.salesContactService.salesContact$;
    this.partnerName$ = this.partnerService.partnerBrandName$;
    this.accountGroupService.currentAccountGroupId$.subscribe((accountGroupId) => {
      this.accountGroupId = accountGroupId;
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    this.setUserGeneralInterest(this.accountGroupId, this.activationMessage).subscribe({
      complete: () => this.close(),
    });
  }

  setUserGeneralInterest(
    accountGroupId: string,
    message = 'Please help me find the right products for our business',
  ): Observable<unknown> {
    return this.http.post('/ajax/v1/registerUserGeneralInterest/', null, {
      params: {
        accountGroupId: accountGroupId,
        message: message,
      },
    });
  }
}
